import React, { useEffect, useState } from "react"
import { useParams, useHistory } from "react-router-dom"
import { 
  Spin, 
  Card, 
  Row, 
  Col, 
  Button, 
  Badge, 
  Drawer, 
  Input, 
  InputNumber, 
  Select, 
  Form, 
  Alert, 
  Typography, 
  Space, 
  Tag, 
  message, 
  Divider,
  Result
} from "antd"
import { 
  ShoppingCartOutlined, 
  WhatsAppOutlined, 
  DeleteOutlined, 
  CheckCircleOutlined,
  ShopOutlined,
  SearchOutlined,
  ReloadOutlined
} from "@ant-design/icons"
import { usePublicStoreCatalog } from "../../hooks/useEcommerce"
import ecommerceService from "../../services/ecommerceService"

const { Title, Text, Paragraph } = Typography
const { Option } = Select

export interface CartItem {
  item_id: number
  item_uid?: string
  name: string
  code?: string
  category?: string
  price: number
  quantity: number
  available_quantity: number
}

export function PublicStorefront() {
  const { storeSlug } = useParams<{ storeSlug: string }>()
  const history = useHistory()

  const {
    store: storeData,
    catalog,
    error,
    isLoading: loading,
    refetch
  } = usePublicStoreCatalog(storeSlug)

  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL")

  const [cart, setCart] = useState<CartItem[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [documentChecked, setDocumentChecked] = useState(false)
  const [checkingDoc, setCheckingDoc] = useState(false)

  const [form] = Form.useForm()

  const cartStorageKey = `ryo_cart_${storeSlug}`

  useEffect(() => {
    try {
      const saved = localStorage.getItem(cartStorageKey)
      if (saved) {
        setCart(JSON.parse(saved))
      }
    } catch {
      // Ignore JSON parse errors
    }
  }, [cartStorageKey])

  const saveCart = (newCart: CartItem[]) => {
    setCart(newCart)
    try {
      localStorage.setItem(cartStorageKey, JSON.stringify(newCart))
    } catch {
      // Ignore storage errors
    }
  }

  const addToCart = (item: any) => {
    const existing = cart.find(c => c.item_id === item.id)
    const available = Number(item.stock_quantity ?? item.quantity_available ?? 9999)
    const allowNoStock = storeData?.allow_order_without_stock
    const itemPrice = Number(item.sale_price ?? item.price ?? item.unit_price) || 0
    const itemCode = item.code || item.sku || item.barcode || ""

    if (existing) {
      if (!allowNoStock && existing.quantity >= available) {
        message.warning(`No hay más unidades disponibles de ${item.name}`)
        return
      }
      const updated = cart.map(c => 
        c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c
      )
      saveCart(updated)
    } else {
      if (!allowNoStock && available <= 0) {
        message.warning(`El producto ${item.name} se encuentra agotado`)
        return
      }
      const newItem: CartItem = {
        item_id: item.id,
        item_uid: item.uid,
        name: item.name,
        code: itemCode,
        category: item.category,
        price: itemPrice,
        quantity: 1,
        available_quantity: available
      }
      saveCart([...cart, newItem])
    }
    message.success(`${item.name} agregado al pedido`)
  }

  const updateQuantity = (itemId: number, qty: number) => {
    if (qty <= 0) {
      removeItem(itemId)
      return
    }
    const item = cart.find(c => c.item_id === itemId)
    const allowNoStock = storeData?.allow_order_without_stock
    if (item && !allowNoStock && qty > item.available_quantity) {
      message.warning(`Cantidad máxima disponible: ${item.available_quantity}`)
      qty = item.available_quantity
    }
    const updated = cart.map(c => c.item_id === itemId ? { ...c, quantity: qty } : c)
    saveCart(updated)
  }

  const removeItem = (itemId: number) => {
    const updated = cart.filter(c => c.item_id !== itemId)
    saveCart(updated)
  }

  const clearCart = () => {
    saveCart([])
  }

  const checkDocument = async () => {
    const docNumber = form.getFieldValue("document_number")
    const docType = form.getFieldValue("document_type") || "CC"
    if (!docNumber) return

    setCheckingDoc(true)
    try {
      const data = await ecommerceService.checkPublicDocument({
        company_id: storeData?.company_id,
        slug: storeSlug,
        document_number: docNumber,
        document_type: docType
      })
      if (!data.error && (data.matched || data.exists) && data.contact) {
        setDocumentChecked(true)
        form.setFieldsValue({
          name: data.contact.name,
          email: data.contact.email,
          phone: data.contact.phone,
          delivery_address: data.contact.address
        })
        message.info(data.message || "Tus datos han sido identificados en el sistema.")
      } else {
        setDocumentChecked(false)
      }
    } catch {
      setDocumentChecked(false)
    } finally {
      setCheckingDoc(false)
    }
  }

  const handleCheckoutSubmit = async (values: any) => {
    if (cart.length === 0) {
      message.error("El carrito está vacío.")
      return
    }

    const minAmount = Number(storeData?.min_order_amount) || 0
    if (subtotal < minAmount) {
      message.error(`El monto mínimo para ordenar es de $${minAmount.toLocaleString()}`)
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        slug: storeSlug,
        company_id: storeData?.company_id,
        company_uid: storeData?.company_uid || storeData?.company,
        store_id: storeData?.id,
        customer: {
          document_type: values.document_type,
          document_number: values.document_number,
          name: values.name,
          email: values.email,
          phone: values.phone
        },
        delivery_info: {
          delivery_method: values.delivery_modality,
          address: values.delivery_address || "",
          notes: values.notes || ""
        },
        items: cart.map(c => ({
          element_id: c.item_id,
          name: c.name,
          item_name: c.name,
          sku: c.code,
          item_sku: c.code,
          quantity: c.quantity,
          unit_price: c.price
        })),
        notes: values.notes || ""
      }

      const data = await ecommerceService.createPublicOrder(payload)

      if (!data.error && (data.status === 200 || data.success) && (data.order || data.tracking_token)) {
        const orderData = data.order || data
        clearCart()
        setDrawerOpen(false)
        message.success("¡Pedido armado con éxito!")
        const token = orderData.tracking_token
        history.push(`/s/${storeSlug}/order/${token}`)
      } else {
        message.error(data.message || "No se pudo crear el pedido.")
      }
    } catch {
      message.error("Error de comunicación con el servidor al registrar el pedido.")
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "100px 20px" }}>
        <Spin size="large" tip="Cargando catálogo..." />
      </div>
    )
  }

  if (error || !storeData) {
    return (
      <div style={{ padding: "50px 20px" }}>
        <Result
          status="404"
          title="Tienda no disponible"
          subTitle={error || "La tienda que buscas no existe o está temporalmente inactiva."}
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      </div>
    )
  }

  const categories: string[] = ["ALL", ...Array.from(new Set(catalog.map((i: any) => i.category).filter(Boolean))) as string[]]

  const filteredCatalog = catalog.filter((item: any) => {
    const itemCode = item.code || item.sku || item.barcode || ""
    const matchesSearch = !searchQuery || 
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      itemCode.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCat = selectedCategory === "ALL" || item.category === selectedCategory
    return matchesSearch && matchesCat
  })

  const cartTotalItems = cart.reduce((sum: number, i: CartItem) => sum + i.quantity, 0)
  const subtotal = cart.reduce((sum: number, i: CartItem) => sum + (i.price * i.quantity), 0)

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f7fa" }}>
      {/* Top Banner / Navigation */}
      <header style={{ 
        backgroundColor: storeData.primary_color || "#1890ff", 
        color: "#fff", 
        padding: "16px 24px",
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {storeData.logo_url ? (
              <img src={storeData.logo_url} alt="Logo" style={{ height: 40, borderRadius: 4 }} />
            ) : (
              <ShopOutlined style={{ fontSize: 28 }} />
            )}
            <div>
              <Title level={4} style={{ color: "#fff", margin: 0 }}>{storeData.store_name}</Title>
              {storeData.description && (
                <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>{storeData.description}</Text>
              )}
            </div>
          </div>
          
          <Space>
            {storeData.contact_whatsapp && (
              <Button 
                type="text" 
                icon={<WhatsAppOutlined />} 
                style={{ color: "#fff", border: "1px solid rgba(255,255,255,0.4)" }}
                href={`https://wa.me/${storeData.contact_whatsapp.replace(/\D/g, '')}`}
                target="_blank"
              >
                WhatsApp
              </Button>
            )}
            <Badge count={cartTotalItems} offset={[-5, 5]}>
              <Button 
                type="primary" 
                icon={<ShoppingCartOutlined />} 
                onClick={() => setDrawerOpen(true)}
                style={{ backgroundColor: "#fff", color: storeData.primary_color || "#1890ff", fontWeight: 600 }}
              >
                Mi Pedido
              </Button>
            </Badge>
          </Space>
        </div>
      </header>

      {/* Main Container */}
      <main style={{ maxWidth: 1200, margin: "24px auto", padding: "0 16px" }}>
        {/* Welcome Notice */}
        {storeData.welcome_message && (
          <Alert 
            message={storeData.welcome_message} 
            type="info" 
            showIcon 
            style={{ marginBottom: 20 }}
          />
        )}

        {/* Filters */}
        <Card style={{ marginBottom: 24, borderRadius: 8 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={12}>
              <Input 
                placeholder="Buscar por producto o código..." 
                prefix={<SearchOutlined />} 
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} md={12} style={{ display: "flex", justifyContent: "flex-end" }}>
              <Select 
                value={selectedCategory} 
                onChange={setSelectedCategory} 
                style={{ width: 220 }}
              >
                {categories.map((cat: string) => (
                  <Option key={cat} value={cat}>
                    {cat === "ALL" ? "Todas las Categorías" : cat}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Product Grid */}
        <Row gutter={[16, 16]}>
          {filteredCatalog.map((item: any) => {
            const inCart = cart.find(c => c.item_id === item.id)
            const available = Number(item.stock_quantity ?? item.quantity_available) || 0
            const isOutOfStock = !storeData.allow_order_without_stock && available <= 0
            const itemPrice = Number(item.sale_price ?? item.price ?? item.unit_price) || 0
            const itemImage = item.image_url || item.image || item.image_mini || item.images?.[0]?.file_url
            const itemCode = item.code || item.sku || item.barcode

            return (
              <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
                <Card 
                  hoverable 
                  style={{ height: "100%", display: "flex", flexDirection: "column", borderRadius: 8 }}
                  cover={
                    itemImage ? (
                      <img alt={item.name} src={itemImage} style={{ height: 180, objectFit: "cover" }} />
                    ) : (
                      <div style={{ height: 140, background: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <ShopOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />
                      </div>
                    )
                  }
                >
                  <div style={{ flex: 1 }}>
                    {item.category && <Tag color="blue">{item.category}</Tag>}
                    <Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>{item.name}</Title>
                    {itemCode && <Text type="secondary" style={{ fontSize: 12 }}>Cód: {itemCode}</Text>}
                    {item.description && (
                      <Paragraph ellipsis={{ rows: 2 }} type="secondary" style={{ fontSize: 13, marginTop: 4 }}>
                        {item.description}
                      </Paragraph>
                    )}
                  </div>

                  <Divider style={{ margin: "12px 0" }} />

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <Text strong style={{ fontSize: 18, color: "#2f54eb" }}>
                      ${itemPrice.toLocaleString()}
                    </Text>
                    {storeData.show_stock_exact && (
                      <Text type={available > 0 ? "secondary" : "danger"} style={{ fontSize: 12 }}>
                        {available > 0 ? `${available} disponibles` : "Agotado"}
                      </Text>
                    )}
                  </div>

                  {inCart ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Button size="small" onClick={() => updateQuantity(item.id, inCart.quantity - 1)}>-</Button>
                      <Text strong>{inCart.quantity}</Text>
                      <Button size="small" onClick={() => updateQuantity(item.id, inCart.quantity + 1)}>+</Button>
                    </div>
                  ) : (
                    <Button 
                      type="primary" 
                      block 
                      disabled={isOutOfStock}
                      onClick={() => addToCart(item)}
                    >
                      {isOutOfStock ? "Sin Stock" : "Agregar"}
                    </Button>
                  )}
                </Card>
              </Col>
            )
          })}
        </Row>

        {filteredCatalog.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Text type="secondary">No se encontraron productos en este catálogo.</Text>
          </div>
        )}
      </main>

      {/* Cart & Checkout Drawer */}
      <Drawer
        title="Resumen del Pedido"
        width={480}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={null}
      >
        {cart.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <ShoppingCartOutlined style={{ fontSize: 48, color: "#bfbfbf", marginBottom: 16 }} />
            <Paragraph type="secondary">Aún no has agregado productos a tu pedido.</Paragraph>
          </div>
        ) : (
          <div>
            {/* Cart Items List */}
            <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 16 }}>
              {cart.map((item: CartItem) => (
                <div 
                  key={item.item_id} 
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}
                >
                  <div style={{ flex: 1, marginRight: 8 }}>
                    <Text strong>{item.name}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        ${item.price.toLocaleString()} x {item.quantity} = ${(item.price * item.quantity).toLocaleString()}
                      </Text>
                    </div>
                  </div>
                  <Space>
                    <InputNumber 
                      min={1} 
                      max={storeData.allow_order_without_stock ? 9999 : item.available_quantity}
                      value={item.quantity} 
                      onChange={(val: number | null) => updateQuantity(item.item_id, Number(val) || 1)}
                      style={{ width: 60 }}
                    />
                    <Button 
                      danger 
                      type="text" 
                      icon={<DeleteOutlined />} 
                      onClick={() => removeItem(item.item_id)} 
                    />
                  </Space>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 16 }}>
              <Text strong>Total estimado:</Text>
              <Text strong style={{ color: "#2f54eb", fontSize: 18 }}>${subtotal.toLocaleString()}</Text>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Mandatory Legal & Process Notice */}
            <Alert 
              type="warning" 
              showIcon 
              message="Información sobre tu pedido" 
              description="Este catálogo permite armar tu pedido para reserva. El pedido quedará en estado PENDIENTE de revisión por parte de nuestro personal. Si los datos ingresados no coinciden o no logran validarse, el pedido no podrá ser procesado ni facturado."
              style={{ marginBottom: 16 }}
            />

            {/* Checkout Form */}
            <Form 
              form={form} 
              layout="vertical" 
              onFinish={handleCheckoutSubmit}
              initialValues={{
                document_type: "CC",
                delivery_modality: storeData.delivery_modalities === "DELIVERY_ONLY" ? "DELIVERY" : "PICKUP"
              }}
            >
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="document_type" label="Tipo Doc" rules={[{ required: true }]}>
                    <Select>
                      <Option value="CC">CC</Option>
                      <Option value="NIT">NIT</Option>
                      <Option value="CE">CE</Option>
                      <Option value="PASSPORT">Pasaporte</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item 
                    name="document_number" 
                    label="N° Documento" 
                    rules={[{ required: true, message: "Ingresa tu número de documento" }]}
                    extra={documentChecked ? <Text type="success"><CheckCircleOutlined /> Documento verificado</Text> : null}
                  >
                    <Input 
                      placeholder="Ej. 1020304050" 
                      onBlur={checkDocument} 
                      disabled={checkingDoc}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="name" label="Nombre Completo / Razón Social" rules={[{ required: true, message: "Ingresa tu nombre" }]}>
                <Input placeholder="Nombre del cliente" />
              </Form.Item>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="phone" label="Teléfono / WhatsApp" rules={[{ required: true, message: "Ingresa tu teléfono" }]}>
                    <Input placeholder="Ej. 3001234567" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="email" label="Correo Electrónico">
                    <Input type="email" placeholder="cliente@correo.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="delivery_modality" label="Modalidad de Entrega" rules={[{ required: true }]}>
                <Select>
                  {storeData.delivery_modalities !== "DELIVERY_ONLY" && (
                    <Option value="PICKUP">Recoger en tienda / Almacén</Option>
                  )}
                  {storeData.delivery_modalities !== "PICKUP_ONLY" && (
                    <Option value="DELIVERY">Envío a domicilio</Option>
                  )}
                </Select>
              </Form.Item>

              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.delivery_modality !== cur.delivery_modality}>
                {({ getFieldValue }) => 
                  getFieldValue("delivery_modality") === "DELIVERY" ? (
                    <Form.Item name="delivery_address" label="Dirección de Envío" rules={[{ required: true, message: "Indica tu dirección" }]}>
                      <Input placeholder="Calle, número, barrio y ciudad" />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>

              <Form.Item name="notes" label="Notas adicionales">
                <Input.TextArea rows={2} placeholder="Instrucciones especiales para el pedido o entrega..." />
              </Form.Item>

              <Button 
                type="primary" 
                htmlType="submit" 
                block 
                size="large" 
                loading={submitting}
                style={{ backgroundColor: storeData.primary_color || "#1890ff", marginTop: 8 }}
              >
                Enviar y Armar Pedido
              </Button>
            </Form>
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default PublicStorefront

